import { useRef, useState } from 'react';
import { ImageOff, Plus, ShieldAlert, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useGuideScreenshots, useReleaseNoteImage, useUploadHighlightImage } from '@/queries/releaseNotes';
import { areaName } from '@/lib/productAreas';
import { cn } from '@/lib/utils';
import { MAX_HIGHLIGHTS, type DraftHighlight, type DraftItem } from './editorState';
import { selectClass } from './ItemEditor';

function Thumb({ s3Key, alt, className }: { s3Key: string | null; alt: string; className?: string }) {
  const image = useReleaseNoteImage(s3Key);
  return (
    <div className={cn('relative overflow-hidden rounded-etn-menu border border-etn-border-hairline bg-etn-surface-sunken', className)}>
      {!s3Key && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-etn-text-soft">
          <ImageOff className="h-5 w-5" aria-hidden />
          <span className="text-etn-micro font-semibold">No image yet</span>
        </div>
      )}
      {image.isLoading && <Skeleton className="absolute inset-0 rounded-none" />}
      {image.data && <img src={image.data} alt={alt} className="absolute inset-0 h-full w-full object-cover object-top" />}
    </div>
  );
}

function ScreenshotPicker({ area, selected, onPick }: { area: string; selected: string | null; onPick: (s3Key: string, alt: string) => void }) {
  const { data, isLoading } = useGuideScreenshots(area);
  const frames = (data ?? []).flatMap(guide => guide.frames.map(frame => ({ ...frame, guide: guide.title })));
  if (isLoading) return <p className="text-etn-cell text-etn-text-label">Loading screenshots…</p>;
  if (!frames.length) return <p className="text-etn-cell text-etn-text-label">No guide screenshots in {areaName(area)}. Upload one instead.</p>;
  return (
    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label={`Screenshots from ${areaName(area)} guides`}>
      {frames.map(frame => (
        <li key={frame.s3Key}>
          <button
            type="button"
            onClick={() => onPick(frame.s3Key, `${frame.guide}`)}
            aria-pressed={selected === frame.s3Key}
            className={cn(
              'block w-full rounded-etn-menu text-left outline-offset-2 focus-visible:outline-2 focus-visible:outline-etn-accent',
              selected === frame.s3Key && 'outline-2 outline-etn-accent'
            )}
          >
            <Thumb s3Key={frame.s3Key} alt="" className="aspect-[16/10]" />
            <span className="mt-1 block truncate text-etn-micro text-etn-text-label">{frame.guide}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function HighlightSlot({
  index,
  highlight,
  items,
  takenKeys,
  onChange,
  onRemove
}: {
  index: number;
  highlight: DraftHighlight;
  items: DraftItem[];
  takenKeys: Set<string>;
  onChange: (patch: Partial<DraftHighlight>) => void;
  onRemove: () => void;
}) {
  const [picking, setPicking] = useState(!highlight.imageS3Key);
  const upload = useUploadHighlightImage();
  const fileInput = useRef<HTMLInputElement>(null);
  const item = items.find(i => i.key === highlight.itemKey);
  const id = (field: string) => `highlight-${index}-${field}`;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const { s3Key } = await upload.mutateAsync(file);
      onChange({ imageS3Key: s3Key });
      setPicking(false);
    } catch (err) {
      toast.error((err as Error).message || 'Could not upload the image');
    }
  };

  return (
    <li className="grid gap-5 py-6 first:pt-0 md:grid-cols-[16rem_1fr]" data-testid="highlight-slot">
      <div className="space-y-2">
        <Thumb s3Key={highlight.imageS3Key} alt={highlight.imageAlt} className="aspect-[16/10]" />
        <Button type="button" size="sm" variant="outline" onClick={() => setPicking(p => !p)} disabled={!item}>
          {picking ? 'Close' : 'Change image'}
        </Button>
      </div>
      <div className="min-w-0 space-y-3">
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor={id('item')}>Highlight {index + 1}</Label>
            <select id={id('item')} className={selectClass} value={highlight.itemKey} onChange={e => onChange({ itemKey: e.target.value })}>
              {items
                .filter(i => i.key === highlight.itemKey || !takenKeys.has(i.key))
                .map(i => (
                  <option key={i.key} value={i.key}>
                    {i.title || '(untitled note)'} · {areaName(i.area)}
                  </option>
                ))}
            </select>
          </div>
          <Button type="button" size="icon-sm" variant="ghost" aria-label={`Remove highlight ${index + 1}`} onClick={onRemove}>
            <Trash2 className="text-etn-danger-text" />
          </Button>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={id('alt')}>Image description</Label>
          <Input
            id={id('alt')}
            value={highlight.imageAlt}
            onChange={e => onChange({ imageAlt: e.target.value })}
            placeholder="What the screenshot shows"
          />
        </div>
        {picking && item && (
          <div className="space-y-3 rounded-etn-menu border border-etn-border-hairline p-3">
            <ScreenshotPicker
              area={item.area}
              selected={highlight.imageS3Key}
              onPick={(s3Key, alt) => {
                onChange({ imageS3Key: s3Key, imageAlt: highlight.imageAlt || alt });
                setPicking(false);
              }}
            />
            <div className="flex flex-wrap items-center gap-3 border-t border-etn-border-hairline pt-3">
              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={e => onFile(e.target.files?.[0])}
              />
              <Button type="button" size="sm" variant="secondary" disabled={upload.isPending} onClick={() => fileInput.current?.click()}>
                <Upload />
                {upload.isPending ? 'Uploading…' : 'Upload a screenshot'}
              </Button>
              <p className="flex items-center gap-1.5 text-etn-cell text-etn-warning-text">
                <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
                Use demo data only, never a real customer’s screen.
              </p>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

export function HighlightsEditor({
  items,
  highlights,
  onChange
}: {
  items: DraftItem[];
  highlights: DraftHighlight[];
  onChange: (next: DraftHighlight[]) => void;
}) {
  const taken = new Set(highlights.map(h => h.itemKey));
  const free = items.filter(item => !taken.has(item.key));
  return (
    <div className="space-y-4">
      <p className="max-w-[65ch] text-etn-cell text-etn-text-label">
        Up to three changes lead the edition with a screenshot, most useful first. A quiet week can have fewer. Every highlight needs an image before
        the edition can be approved.
      </p>
      {highlights.length > 0 && (
        <ul className="divide-y divide-etn-border-hairline">
          {highlights.map((highlight, i) => (
            <HighlightSlot
              key={`${i}-${highlight.itemKey}`}
              index={i}
              highlight={highlight}
              items={items}
              takenKeys={taken}
              onChange={patch => onChange(highlights.map((h, j) => (j === i ? { ...h, ...patch } : h)))}
              onRemove={() => onChange(highlights.filter((_, j) => j !== i))}
            />
          ))}
        </ul>
      )}
      {highlights.length < MAX_HIGHLIGHTS && free.length > 0 && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="border-dashed"
          onClick={() => onChange([...highlights, { itemKey: free[0].key, imageS3Key: null, imageAlt: '' }])}
        >
          <Plus />
          Add a highlight
        </Button>
      )}
    </div>
  );
}
